import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import UserCreateDrawer from '../components/UserCreateDrawer';
import UserDetailDrawer from '../components/drawers/UserDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Search, Download, UserPlus, ChevronLeft, ChevronRight, Gem, Star, Filter } from 'lucide-react';
const Users = () => {
  const location = useLocation();
  const {
    users
  } = useAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const usersPerPage = 10;
  // Check if we should open the drawer from navigation state
  useEffect(() => {
    if (location.state?.openDrawer) {
      setShowCreateDrawer(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  // Get unique cities for filter
  const uniqueCities = useMemo(() => {
    const cities = users.map(u => u.luogo).filter(Boolean);
    return ['all', ...Array.from(new Set(cities))];
  }, [users]);
  // Filter users with useMemo
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = user.nome.toLowerCase().includes(searchTerm.toLowerCase()) || user.cognome.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      const matchesCity = cityFilter === 'all' || user.luogo === cityFilter;
      return matchesSearch && matchesStatus && matchesCity;
    });
  }, [users, searchTerm, statusFilter, cityFilter]);
  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const handleExport = () => {
    console.log('Export CSV');
  };
  const handleAddUser = () => {
    setShowCreateDrawer(true);
  };
  const handleRowClick = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => setSelectedUserId(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-3647">
      <div className="w-20 flex-shrink-0" data-id="element-3648">
        <Sidebar onAddUser={handleAddUser} data-id="element-3649" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3650">
        <Topbar data-id="element-3651" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3652">
          <PageHeader title="Utenti" subtitle="Gestisci tutti gli utenti della piattaforma Greenback" primaryAction={{
          label: 'Aggiungi Utente',
          onClick: handleAddUser,
          icon: <UserPlus className="h-4 w-4 mr-2" data-id="element-3654" />
        }} secondaryAction={{
          label: 'Esporta CSV',
          onClick: handleExport,
          icon: <Download className="h-4 w-4 mr-2" data-id="element-3655" />
        }} data-id="element-3653" />

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-3656">
            <div className="relative flex-1 min-w-[300px]" data-id="element-3657">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-3658" />
              <input type="text" placeholder="Cerca per nome, cognome o email..." className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} data-id="element-3659" />
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-3660">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-3661" />
              <select className="bg-transparent text-sm focus:outline-none" value={statusFilter} onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-3662">
                <option value="all" data-id="element-3663">Tutti gli stati</option>
                <option value="active" data-id="element-3664">Attivo</option>
                <option value="inactive" data-id="element-3665">Inattivo</option>
                <option value="suspended" data-id="element-3666">Sospeso</option>
              </select>
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-3667">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-3668" />
              <select className="bg-transparent text-sm focus:outline-none" value={cityFilter} onChange={e => {
              setCityFilter(e.target.value);
              setCurrentPage(1);
            }} data-id="element-3669">
                <option value="all" data-id="element-3670">Tutte le città</option>
                {uniqueCities.filter(city => city !== 'all').map(city => <option key={city} value={city} data-id="element-3671">
                      {city}
                    </option>)}
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-3672">
            <div className="overflow-x-auto" data-id="element-3673">
              <table className="w-full" data-id="element-3674">
                <thead data-id="element-3675">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3676">
                    <th className="px-6 py-3" data-id="element-3677">Utente</th>
                    <th className="px-6 py-3" data-id="element-3678">Email</th>
                    <th className="px-6 py-3" data-id="element-3679">Luogo</th>
                    <th className="px-6 py-3" data-id="element-3680">Obiettivi</th>
                    <th className="px-6 py-3" data-id="element-3681">
                      <div className="flex items-center gap-1" data-id="element-3682">
                        <Gem className="h-3 w-3" data-id="element-3683" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-3684">
                      <div className="flex items-center gap-1" data-id="element-3685">
                        <Gem className="h-3 w-3" data-id="element-3686" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-3687">
                      <div className="flex items-center gap-1" data-id="element-3688">
                        <Star className="h-3 w-3" data-id="element-3689" />
                        Ottenuti
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-3690">
                      <div className="flex items-center gap-1" data-id="element-3691">
                        <Star className="h-3 w-3" data-id="element-3692" />
                        Spesi
                      </div>
                    </th>
                    <th className="px-6 py-3" data-id="element-3693">Riscatti</th>
                    <th className="px-6 py-3 text-right" data-id="element-3694">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3695">
                  {currentUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(user.id)} data-id="element-3696">
                      <td className="px-6 py-4" data-id="element-3697">
                        <div className="flex items-center" data-id="element-3698">
                          <img src={user.avatar} alt={`${user.nome} ${user.cognome}`} className="h-10 w-10 rounded-full object-cover mr-3" data-id="element-3699" />
                          <div data-id="element-3700">
                            <div className="font-medium text-gray-900" data-id="element-3701">
                              {user.nome} {user.cognome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3702">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3703">
                        {user.luogo}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3704">
                        {user.obiettivi}
                      </td>
                      <td className="px-6 py-4 font-medium text-blue-600" data-id="element-3705">
                        {user.creditiDiamanteOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3706">
                        {user.creditiDiamanteSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-amber-600" data-id="element-3707">
                        {user.creditiStellaOttenuti.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3708">
                        {user.creditiStellaSpesi.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3709">
                        {user.claimFatti}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-3710">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRowClick(user.id);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-3711">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-3712">
              <div className="text-sm text-gray-500" data-id="element-3713">
                Mostrando {indexOfFirstUser + 1} -{' '}
                {Math.min(indexOfLastUser, filteredUsers.length)} di{' '}
                {filteredUsers.length} utenti
              </div>
              <div className="flex items-center gap-2" data-id="element-3714">
                <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3715">
                  <ChevronLeft className="h-4 w-4" data-id="element-3716" />
                </button>
                <span className="text-sm text-gray-700" data-id="element-3717">
                  Pagina {currentPage} di {totalPages}
                </span>
                <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3718">
                  <ChevronRight className="h-4 w-4" data-id="element-3719" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* User Create Drawer */}
      <UserCreateDrawer isOpen={showCreateDrawer} onClose={() => setShowCreateDrawer(false)} onSuccess={() => {
      console.log('User created successfully');
    }} data-id="element-3720" />

      {/* User Detail Drawer */}
      <UserDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} userId={selectedUserId} data-id="element-3721" />
    </div>;
};
export default Users;